import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { registerDefaultsForModule, withStyles } from '@glu/theming';
import { CircleSolidIcon, CheckmarkCircleNextIcon, CircleNextIcon } from '@glu/icons-react';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === 'function') {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _defineProperty(target, key, source[key]);
    });
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

/* istanbul ignore file */
var legacy = (function (_ref) {
  var _ref$palette = _ref.palette,
      accent = _ref$palette.accent,
      light = _ref$palette.text.light,
      darkBackground = _ref$palette.primary.darkBackground,
      G600 = _ref$palette.grey.G600,
      background = _ref$palette.background,
      fontFamily = _ref.typography.text.fontFamily;
  return {
    paddingForConnection: 11,
    primaryColor: '#0472BA',
    passedStep: "1px solid ".concat(accent),
    connection: "1px solid ".concat(accent),
    darkBackground: darkBackground,
    lightBackground: light,
    accent: accent,
    lightColor: '#4c4c4c',
    grey: G600,
    borderColor: accent,
    paddingForConnectionIcon: 0,
    darkColor: light,
    background: background,
    fontFamily: fontFamily,
    identifierStyles: {},
    verticalConnection: {
      minHeight: 150,
      borderLeft: "2px solid ".concat(accent)
    }
  };
});

/* istanbul ignore file */
var ash = (function (_ref) {
  var _ref$palette = _ref.palette,
      accent = _ref$palette.accent,
      light = _ref$palette.text.light,
      _ref$palette$primary = _ref$palette.primary,
      darkBackground = _ref$palette$primary.darkBackground,
      normal = _ref$palette$primary.normal,
      G600 = _ref$palette.grey.G600,
      background = _ref$palette.background,
      fontFamily = _ref.typography.text.fontFamily;
  return {
    paddingForConnection: 11,
    primaryColor: normal,
    passedStep: "2px solid ".concat(normal),
    connection: "2px solid ".concat(accent),
    darkBackground: darkBackground,
    lightBackground: light,
    accent: accent,
    lightColor: '#4c4c4c',
    grey: G600,
    borderColor: accent,
    paddingForConnectionIcon: 0,
    darkColor: light,
    background: background,
    fontFamily: fontFamily,
    identifierStyles: {},
    verticalConnection: {
      minHeight: 150,
      borderLeft: "2px solid ".concat(accent)
    }
  };
});

var themes = {
  ash: ash,
  legacy: legacy
};
var defaultsRegistered = false;
function registerStepsDefaults() {
  if (!defaultsRegistered) {
    registerDefaultsForModule('steps', function (theme) {
      return themes[theme.name](theme);
    });
    defaultsRegistered = true;
  }
}

var styles = (function (_ref) {
  var _ref$steps = _ref.steps,
      darkBackground = _ref$steps.darkBackground,
      lightBackground = _ref$steps.lightBackground,
      lightColor = _ref$steps.lightColor,
      primaryColor = _ref$steps.primaryColor,
      paddingForConnectionIcon = _ref$steps.paddingForConnectionIcon,
      accent = _ref$steps.accent,
      darkColor = _ref$steps.darkColor,
      identifierStyles = _ref$steps.identifierStyles;

  var identifierStylesCommon = _objectSpread({
    backgroundColor: function backgroundColor(_ref2) {
      var dark = _ref2.dark;
      return dark ? darkBackground : lightBackground;
    },
    fontWeight: function fontWeight(_ref3) {
      var dark = _ref3.dark;
      return dark ? 400 : 600;
    },
    color: function color(_ref4) {
      var dark = _ref4.dark;
      return dark ? darkColor : lightColor;
    },
    height: 20,
    width: 20,
    minWidth: 20,
    minHeight: 20,
    borderRadius: '50%'
  }, identifierStyles);

  return {
    actionable: {
      cursor: 'pointer',
      '& svg:hover': {
        borderRadius: '50%',
        fill: primaryColor,
        backgroundColor: primaryColor
      }
    },
    positive: {
      '& svg': {
        fill: primaryColor
      }
    },
    negative: {
      '& svg': {
        fill: accent
      }
    },
    verticalIconPadding: {
      left: 2
    },
    horizontalIconPadding: {
      top: paddingForConnectionIcon
    },
    activeStepIcon: {
      '& svg': {
        fill: primaryColor
      }
    },
    identifier: _objectSpread(_objectSpread({}, identifierStylesCommon), {}, {
      alignSelf: 'flex-start',
      border: "2px solid ".concat(accent),
      borderRadius: '50%',
      fontSize: 14,
      textAlign: 'center',
      position: 'absolute'
    }),
    iconIdentifier: _objectSpread(_objectSpread({}, identifierStylesCommon), {}, {
      alignSelf: 'flex-start',
      position: 'absolute'
    })
  };
});

registerStepsDefaults();

var iconWrapperClass = function iconWrapperClass(classes, dataRecord, currentStep) {
  return classNames(_defineProperty({}, classes.activeStepIcon, dataRecord[currentStep]));
};

var generateIdentifierClassName = function generateIdentifierClassName(vertical, classes, value, icon) {
  var isActionable = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
  var classesList = [icon ? classes.iconIdentifier : classes.identifier, value ? classes.positive : classes.negative, vertical ? classes.verticalIconPadding : classes.horizontalIconPadding, isActionable ? classes.actionable : ''];
  return classesList.join(' ');
};

var StepIconWithValue = function StepIconWithValue(_ref) {
  var value = _ref.value,
      vertical = _ref.vertical,
      classes = _ref.classes,
      isActive = _ref.isActive,
      numberIdentifier = _ref.numberIdentifier,
      dataRecord = _ref.dataRecord,
      actionable = _ref.actionable,
      onStepClick = _ref.onStepClick,
      currentStep = _ref.currentStep;
  var isActionable = dataRecord.actionable === false || dataRecord.actionable === true ? dataRecord.actionable : actionable;

  var onClickHandler = function onClickHandler() {
    var callback = isActionable ? dataRecord.onClick || onStepClick : function () {};
    callback(dataRecord);
  };

  return (
    /*#__PURE__*/
    React.createElement(React.Fragment, null, typeof numberIdentifier === 'number' ?
    /*#__PURE__*/
    React.createElement("div", {
      className: generateIdentifierClassName(vertical, classes, null, false)
    }, numberIdentifier + 1) :
    /*#__PURE__*/
    React.createElement("div", {
      className: iconWrapperClass(classes, dataRecord, currentStep)
    },
    /*#__PURE__*/
    React.createElement("div", {
      className: generateIdentifierClassName(vertical, classes, value, true, isActionable)
    }, isActive ?
    /*#__PURE__*/
    React.createElement(CircleSolidIcon, {
      height: 20,
      width: 20,
      focusable: false
    }) :
    /*#__PURE__*/
    React.createElement(React.Fragment, null, value ?
    /*#__PURE__*/
    React.createElement(CheckmarkCircleNextIcon, {
      focusable: false,
      height: 20,
      width: 20,
      onClick: onClickHandler
    }) :
    /*#__PURE__*/
    React.createElement(CircleNextIcon, {
      focusable: false,
      height: 20,
      width: 20,
      onClick: onClickHandler
    })))))
  );
};

StepIconWithValue.propTypes = {
  /**
   * indicates if the icon could be clicked
   */
  actionable: PropTypes.bool,

  /**
   * callback that will be called on icon click
   */
  onStepClick: PropTypes.func,

  /**
   * The structure of data which we use for each step.
   */
  dataRecord: PropTypes.objectOf(PropTypes.any).isRequired,

  /**
   * The parameter which shows the status of step (active step, passed step).
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),

  /**
   * The parameter which defines active step from all steps. React Steps can be without active step.
   */
  currentStep: PropTypes.string,

  /**
   * The parameter which will define position for steps. It can be vertical or horizontal.
   */
  vertical: PropTypes.bool,

  /**
   * The parameter which shows is it active or not.
   */
  isActive: PropTypes.bool,

  /**
   * The parameter which we use to verify what should we display. It can be a numeric value or icon.
   */
  numberIdentifier: PropTypes.oneOfType([PropTypes.number, PropTypes.object // for null value by default
  ]),

  /**
   * JSS Classes
   */
  classes: PropTypes.shape({}).isRequired
};
StepIconWithValue.defaultProps = {
  actionable: false,
  value: '',
  onStepClick: function onStepClick() {},
  vertical: false,
  currentStep: '',
  isActive: false,
  numberIdentifier: null
};
var StepIconWithValue$1 = withStyles(styles)(StepIconWithValue);

var styles$1 = (function (_ref) {
  var _ref$steps = _ref.steps,
      paddingForConnection = _ref$steps.paddingForConnection,
      grey = _ref$steps.grey,
      borderColor = _ref$steps.borderColor,
      verticalConnection = _ref$steps.verticalConnection;
  return {
    root: {
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: paddingForConnection
    },
    listItem: {
      display: 'flex',
      paddingBottom: 20
    },
    verticalConnection: verticalConnection,
    horizontalConnection: {
      minWidth: 150,
      borderTop: "2px solid ".concat(borderColor)
    },
    content: {
      marginLeft: 0,
      width: '100%',
      fontSize: 14,
      color: grey,
      textDecoration: "none solid ".concat(grey),
      lineHeight: '20px',
      position: 'relative',
      marginTop: function marginTop(_ref2) {
        var vertical = _ref2.vertical;
        return vertical ? 0 : 5;
      },
      paddingTop: function paddingTop(_ref3) {
        var vertical = _ref3.vertical;
        return vertical ? 0 : 8;
      },
      paddingLeft: function paddingLeft(_ref4) {
        var vertical = _ref4.vertical;
        return vertical ? 10 : 0;
      },
      '& p': {
        paddingLeft: function paddingLeft(_ref5) {
          var vertical = _ref5.vertical;
          return vertical ? 10 : 0;
        }
      }
    },
    activeStepContent: {
      color: grey,
      fontWeight: 700
    },
    contentHorizontal: {
      textAlign: 'center',
      left: 'calc(-50% + 10px)'
    },
    contentVertical: {
      textAlign: 'start',
      display: 'flex',
      flexDirection: 'column'
    },
    nestedContentHorizontal: {
      left: 'calc(50% - 10px)',
      display: 'flex'
    },
    nestedContentVertical: {
      left: 20
    }
  };
});

registerStepsDefaults();

var contentClass = function contentClass(classes, vertical, dataRecord, currentStep) {
  var _classNames;

  return classNames(classes.content, (_classNames = {}, _defineProperty(_classNames, classes.contentVertical, vertical), _defineProperty(_classNames, classes.contentHorizontal, !vertical), _defineProperty(_classNames, classes.activeStepContent, dataRecord[currentStep]), _classNames));
};

var nestedContentClass = function nestedContentClass(classes, vertical) {
  var _classNames2;

  return classNames(classes.content, (_classNames2 = {}, _defineProperty(_classNames2, classes.nestedContentVertical, vertical), _defineProperty(_classNames2, classes.nestedContentHorizontal, !vertical), _classNames2));
};

var listItemClass = function listItemClass(classes, nestedData, vertical, index) {
  var _classNames3;

  return classNames(classes.listItem, (_classNames3 = {}, _defineProperty(_classNames3, classes.verticalConnection, nestedData.length !== index + 1 && vertical), _defineProperty(_classNames3, classes.horizontalConnection, nestedData.length !== index + 1 && !vertical), _defineProperty(_classNames3, classes.nestedContentHorizontal, !vertical), _classNames3));
};

var NavigationStep = function NavigationStep(_ref) {
  var rootValue = _ref.rootValue,
      NestedDetails = _ref.NestedDetails,
      vertical = _ref.vertical,
      classes = _ref.classes,
      actionable = _ref.actionable,
      onStepClick = _ref.onStepClick,
      dataRecord = _ref.dataRecord,
      positiveNegativeValue = _ref.positiveNegativeValue,
      currentStep = _ref.currentStep,
      DataDetails = _ref.DataDetails,
      hasNestedStep = _ref.hasNestedStep,
      nestedData = _ref.nestedData,
      nestedNumberIdentifierId = _ref.nestedNumberIdentifierId,
      nestedPositiveNegativeValue = _ref.nestedPositiveNegativeValue,
      nestedId = _ref.nestedId,
      numberIdentifier = _ref.numberIdentifier,
      dark = _ref.dark;
  return (
    /*#__PURE__*/
    React.createElement(React.Fragment, null,
    /*#__PURE__*/
    React.createElement(StepIconWithValue$1, {
      vertical: vertical,
      currentStep: currentStep,
      numberIdentifier: numberIdentifier,
      value: dataRecord[positiveNegativeValue],
      isActive: dataRecord[currentStep],
      dataRecord: dataRecord,
      actionable: actionable,
      onStepClick: onStepClick,
      dark: dark
    }),
    /*#__PURE__*/
    React.createElement("div", {
      className: contentClass(classes, vertical, dataRecord, currentStep)
    }, DataDetails &&
    /*#__PURE__*/
    React.createElement(DataDetails, {
      record: dataRecord,
      rootValue: rootValue
    }), NestedDetails &&
    /*#__PURE__*/
    React.createElement(NestedDetails, {
      record: dataRecord
    }), hasNestedStep &&
    /*#__PURE__*/
    React.createElement("div", {
      className: nestedContentClass(classes, vertical)
    }, nestedData.map(function (nestedDataRecord, index) {
      /*
      * If we want to nested steps have a different
      * identifier - `numberIdentifier` we should pass
      * `nestedNumberIdentifierId` to the `NavigationPanel`
      * component.
      */
      var numberIdentifierResult = nestedDataRecord[nestedPositiveNegativeValue] ? null : nestedDataRecord[nestedNumberIdentifierId];
      return (
        /*#__PURE__*/
        React.createElement("div", {
          key: nestedDataRecord[nestedId],
          className: listItemClass(classes, nestedData, vertical, index)
        },
        /*#__PURE__*/
        React.createElement(NavigationStep, {
          dataRecord: nestedDataRecord,
          rootValue: rootValue,
          numberIdentifier: numberIdentifierResult,
          positiveNegativeValue: nestedPositiveNegativeValue,
          dark: dark,
          NestedDetails: NestedDetails,
          hasNestedStep: false,
          vertical: vertical,
          classes: classes
        }))
      );
    }))))
  );
};

NavigationStep.propTypes = {
  /**
   * indicates if the step could be clicked
   */
  actionable: PropTypes.bool,

  /**
   * callback that will be called on step click
   */
  onStepClick: PropTypes.func,

  /**
   * The structure of data which we use for each step.
   */
  dataRecord: PropTypes.objectOf(PropTypes.any).isRequired,

  /**
   * The parameter which we pass inside DataDetails component to display like a title.
   */
  rootValue: PropTypes.string.isRequired,

  /**
   * The parameter which is responsible for the status of each step. Is step done or in working
   * status.
   */
  positiveNegativeValue: PropTypes.string.isRequired,

  /**
   * The parameter which verifies does NavigationStep component have nested steps.
   */
  hasNestedStep: PropTypes.bool,

  /**
   * The list of nested steps.
   */
  nestedData: PropTypes.arrayOf(PropTypes.any),

  /**
   * The parameter which is responsible for the status of each step. Is step done or in working
   * status.
   */
  nestedPositiveNegativeValue: PropTypes.string,

  /**
   * The parameter which verify should we display nested steps with numeric labels.
   */
  nestedNumberIdentifierId: PropTypes.string,

  /**
   * The name of the key which we use like unique identifier inside loop.
   */
  nestedId: PropTypes.string,

  /**
   * Should we apply the dark theme.
   */
  dark: PropTypes.bool,

  /**
   * The parameter which will define position for steps. It can be vertical or horizontal.
   */
  vertical: PropTypes.bool,

  /**
   * The parameter which defines active step from all steps. React Steps can be without active step.
   */
  currentStep: PropTypes.string,

  /**
   * The numeric value for the case when we have number identifier.
   */
  numberIdentifier: PropTypes.oneOfType([PropTypes.number, PropTypes.object // for null value by default
  ]),

  /**
   * The component which we use to display custom content for each nested step.
   * It will be a custom component which we pass to the NavigationPanel. You can look at examples
   * by the path '/stories/components/'.
   */
  NestedDetails: PropTypes.oneOfType([PropTypes.object, // for null value by default
  PropTypes.func]),

  /**
   * The component which we use to display custom content for each step. It will be a custom
   * component which we pass to the NavigationPanel. You can look at examples
   * by the path '/stories/components/'.
   */
  DataDetails: PropTypes.oneOfType([PropTypes.object, // for null value by default
  PropTypes.func]),

  /**
   * JSS Classes
   */
  classes: PropTypes.objectOf(PropTypes.string).isRequired
};
NavigationStep.defaultProps = {
  actionable: false,
  hasNestedStep: false,
  onStepClick: function onStepClick() {},
  nestedData: [],
  nestedNumberIdentifierId: '',
  nestedPositiveNegativeValue: '',
  nestedId: '',
  vertical: false,
  currentStep: '',
  DataDetails: null,
  NestedDetails: null,
  numberIdentifier: null,
  dark: false
};
var NavigationStep$1 = withStyles(styles$1)(NavigationStep);

var styles$2 = (function (_ref) {
  var _ref$steps = _ref.steps,
      paddingForConnection = _ref$steps.paddingForConnection,
      verticalConnection = _ref$steps.verticalConnection,
      connection = _ref$steps.connection,
      passedStep = _ref$steps.passedStep,
      background = _ref$steps.background,
      fontFamily = _ref$steps.fontFamily;
  return {
    root: {
      fontFamily: fontFamily,
      display: 'flex',
      paddingTop: 10,
      justifyContent: 'center',
      flexDirection: function flexDirection(_ref2) {
        var vertical = _ref2.vertical;
        return vertical && 'column';
      },
      position: 'relative',
      paddingLeft: function paddingLeft(_ref3) {
        var vertical = _ref3.vertical;
        return vertical ? paddingForConnection : 0;
      }
    },
    stickyTop: {
      position: 'fixed',
      top: 0,
      width: '100%',
      boxShadow: '0 5px 4px -4px rgba(0, 0, 0, 0.2)',
      background: background
    },
    navigationHolder: {
      paddingTop: 20,
      width: '100%'
    },
    listItem: {
      display: 'flex',
      paddingBottom: 20,
      '&:last-child': {
        borderTop: function borderTop(_ref4) {
          var vertical = _ref4.vertical;
          return vertical ? undefined : connection;
        },
        borderColor: function borderColor(_ref5) {
          var vertical = _ref5.vertical;
          return vertical ? undefined : 'transparent';
        }
      }
    },
    verticalConnection: verticalConnection,
    horizontalConnection: {
      minWidth: 150,
      borderTop: connection
    },
    horizontalPassedStep: {
      borderTop: passedStep
    },
    verticalPassedStep: {
      borderLeft: passedStep
    }
  };
});

var _excluded = ["data"];
registerStepsDefaults();

var connectionClass = function connectionClass(_ref) {
  var _classNames;

  var classes = _ref.classes,
      dataRecord = _ref.dataRecord,
      positiveNegativeValue = _ref.positiveNegativeValue,
      currentStep = _ref.currentStep,
      data = _ref.data,
      vertical = _ref.vertical,
      index = _ref.index;
  return classNames(classes.listItem, (_classNames = {}, _defineProperty(_classNames, classes.verticalConnection, data.length !== index + 1 && vertical), _defineProperty(_classNames, classes.horizontalConnection, data.length !== index + 1 && !vertical), _defineProperty(_classNames, classes.verticalPassedStep, currentStep && dataRecord[positiveNegativeValue] && vertical), _defineProperty(_classNames, classes.horizontalPassedStep, currentStep && dataRecord[positiveNegativeValue] && !vertical), _classNames));
};

var NavigationPanel = function NavigationPanel(props) {
  var rootValue = props.rootValue,
      positiveNegativeValue = props.positiveNegativeValue,
      nestedData = props.nestedData,
      nestedRoot = props.nestedRoot,
      currentStep = props.currentStep,
      numberIdentifier = props.numberIdentifier,
      stickyTop = props.stickyTop,
      vertical = props.vertical,
      classes = props.classes,
      idField = props.idField,
      className = props.className; // We use this to exclude data from props which we want to pass to NavigationStep

  var data = props.data,
      updatedProps = _objectWithoutProperties(props, _excluded);

  var navigationPanelRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      outOfVision = _useState2[0],
      setOutOfVision = _useState2[1];

  var _useState3 = useState(undefined),
      _useState4 = _slicedToArray(_useState3, 2),
      originalPanelPosition = _useState4[0],
      setOriginalPanelPosition = _useState4[1];

  useEffect(function () {
    var handleScroll = function handleScroll() {
      if (window.pageYOffset > originalPanelPosition && !outOfVision) {
        setOutOfVision(true);
      } else if (window.pageYOffset <= originalPanelPosition && outOfVision) {
        setOutOfVision(false);
      }
    };

    if (stickyTop) {
      if (!originalPanelPosition) setOriginalPanelPosition(navigationPanelRef.current.offsetTop);
      window.addEventListener('scroll', handleScroll);
    }

    return function () {
      if (stickyTop) window.removeEventListener('scroll', handleScroll);
    };
  }, [navigationPanelRef, stickyTop, outOfVision, originalPanelPosition]);
  return (
    /*#__PURE__*/
    React.createElement("div", {
      ref: navigationPanelRef,
      className: "".concat(classes.navigationHolder, " ").concat(className, " ").concat(outOfVision && stickyTop ? classes.stickyTop : '')
    },
    /*#__PURE__*/
    React.createElement("div", {
      className: classNames(classes.root)
    }, data.map(function (dataRecord, index) {
      return (
        /*#__PURE__*/
        React.createElement("div", {
          key: dataRecord[idField],
          className: connectionClass({
            classes: classes,
            dataRecord: dataRecord,
            positiveNegativeValue: positiveNegativeValue,
            currentStep: currentStep,
            data: data,
            vertical: vertical,
            index: index
          })
        },
        /*#__PURE__*/
        React.createElement(NavigationStep$1 // eslint-disable-next-line react/jsx-props-no-spreading
        , _extends({}, updatedProps, {
          dataRecord: dataRecord,
          hasNestedStep: dataRecord[rootValue] === nestedRoot && Boolean(nestedData.length),
          numberIdentifier: numberIdentifier ? index : undefined
        })))
      );
    })))
  );
};

NavigationPanel.propTypes = {
  /**
   * The list of steps. Each step should be an object with custom fields.
   * Any step can have nested steps.
   */
  data: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,

  /**
   * The parameter which will be used to verify existing nested steps inside input data
   * and value of this parameter will be displayed like the title for each step.
   */
  rootValue: PropTypes.string.isRequired,

  /**
   * The parameter which is responsible for the status of each step. Is step done or in working
   * status.
   */
  positiveNegativeValue: PropTypes.string.isRequired,

  /**
   * The name of the key which we use like unique identifier inside loop.
   */
  idField: PropTypes.string,

  /**
   * The list of nested steps.
   */
  nestedData: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),

  /**
   * The parameter which will be used to verify existing nested steps inside input data.
   */
  nestedRoot: PropTypes.string,

  /**
   * The parameter which will be used to verify does each step have a number label or an icon.
   */
  numberIdentifier: PropTypes.bool,

  /**
   * The parameter which will define position for steps. It can be vertical or horizontal.
   */
  vertical: PropTypes.bool,

  /**
   * The parameter which defines active step from all steps. Steps can be used without active step.
   */
  currentStep: PropTypes.string,

  /**
   * JSS Classes
   */
  classes: PropTypes.objectOf(PropTypes.string).isRequired,

  /**
   * Custom className passed from outside
   */
  className: PropTypes.string,

  /**
   * Indicates whether the navigation panel should stick to the top while scrolling
   */
  stickyTop: PropTypes.bool
};
NavigationPanel.defaultProps = {
  className: '',
  idField: 'id',
  nestedData: [],
  nestedRoot: '',
  numberIdentifier: true,
  vertical: false,
  stickyTop: false,
  currentStep: ''
};
var StyledNavigationPanel = withStyles(styles$2)(NavigationPanel);
StyledNavigationPanel.displayName = 'NavigationPanel';

export default StyledNavigationPanel;
